import React, { useCallback, useState } from 'react';
import { useChat } from '../../hooks/useChat';
import { ChatMessages } from './ChatMessages';
import { Brouillon, ChatInput } from './ChatInput';
import { PistesDeSuite } from './PistesDeSuite';
import { Activity, BarChart3, LineChart, LucideIcon, Scale, Sparkles, Table2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import './chat.css';

interface Depart {
  categorie: string;
  question: string;
  Icone: LucideIcon;
  reservePro?: boolean;
}

// Suggestions d'accueil choisies à la main (cf. docs/nia_interface/) : spécifiques, et
// représentatives de ce que les outils de Nia savent réellement faire. Les sociétés
// citées sont celles du compte démo (SNTS, ORAC, CIEC), pour qu'aucune ne soit refusée.
const DEPARTS: Depart[] = [
  { categorie: 'Comparer', question: 'Compare Sonatel et Orange CI au BRVM Composite sur 6 mois', Icone: LineChart },
  { categorie: 'Dividendes', question: "Montre l'historique des dividendes de Sonatel sur 10 ans", Icone: BarChart3 },
  { categorie: 'Marché', question: 'Comment a évolué le BRVM Composite depuis 6 mois ?', Icone: Activity },
  { categorie: 'Valorisation', question: "Quel est l'Indice de Valeur Kanea de la CIE ?", Icone: Scale, reservePro: true },
  { categorie: 'Comptes', question: 'Affiche les comptes annuels de la CIE sur 5 ans', Icone: Table2 },
];
const NB_DEPARTS = 4;

export const ChatContent: React.FC = () => {
  const { messages, isLoading, isPaidUser, sendMessage, error, avertissement } = useChat();
  const { user } = useAuth();
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const firstName = user?.full_name ? user.full_name.split(' ')[0] : null;
  const alerte = error || avertissement;
  const departs = DEPARTS.filter((d) => isPaidUser || !d.reservePro).slice(0, NB_DEPARTS);

  const reformuler = useCallback((texte: string) => setBrouillon({ texte, id: Date.now() }), []);

  return (
    <div className="chat-content-container">
      {alerte && (
        <div role="status" className={`chat-alerte ${error ? 'chat-alerte--erreur' : 'chat-alerte--avertissement'}`}>
          {alerte}
        </div>
      )}
      {messages.length === 0 && !isLoading ? (
        <div className="chat-welcome">
          <div className="welcome-top-zone">
            <div className="nia-square-icon">
              <Sparkles size={24} color="white" fill="white" />
            </div>
            <h1 className="welcome-greeting">{firstName ? `Bonjour ${firstName}` : 'Bonjour'}</h1>
            <p className="welcome-subtitle">
              Cours, dividendes, comptes, valorisation : posez une question, ou partez d'un exemple.
            </p>
          </div>

          <div className="welcome-input-zone">
            <ChatInput onSendMessage={sendMessage} isLoading={isLoading} isPaidUser={isPaidUser} isWelcomeMode={true} />
            <div className="departs-grille">
              {departs.map(({ categorie, question, Icone }) => (
                <button key={question} type="button" className="depart-carte" onClick={() => sendMessage(question)}>
                  <span className="depart-categorie">
                    <Icone size={14} aria-hidden="true" />
                    {categorie}
                  </span>
                  <span className="depart-question">{question}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="welcome-bottom-spacer" />
        </div>
      ) : (
        <>
          <ChatMessages messages={messages} onReformuler={reformuler} />
          <PistesDeSuite messages={messages} isLoading={isLoading} onChoisir={sendMessage} />
          <ChatInput onSendMessage={sendMessage} isLoading={isLoading} isPaidUser={isPaidUser} brouillon={brouillon} />
        </>
      )}
    </div>
  );
};
